import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import ChevronLeft from '@pedidosya/web-fenix/icons/ChevronLeft';
import Close from '@pedidosya/web-fenix/icons/Close';

import SimpleButton, { SIZE } from '@components/SimpleButton';
import Logo from '@shared/components/Logo';
import { onKeyDownFunction, onKeyUpFunction } from '@utils/a11y';

const FIXED_HEADER_ID = 'fixed_header';

const StyledFixedHeader = styled.div`
  ${({ theme }) => theme.fontFamily.mainFont.mediumExtraBold}
  width: 100%;
  background: ${({ theme }) => theme.commons.header.background};
  z-index: ${({ theme }) => theme.commons.header.zIndexFixedHeader};
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 44px;
  position: relative;
  justify-content: center;
  ${({ shadow, theme }) =>
    shadow &&
    css`
      box-shadow: ${theme.commons.header.elevation};
    `}
  ${({ fixed }) =>
    fixed &&
    css`
      position: fixed;
      top: 0;
    `}
`;

const HeaderLogo = styled(Logo)`
  width: 87px;
  margin-left: 45px;
`;

const Icon = styled.img`
  position: absolute;
  cursor: pointer;
  z-index: 1;
`;

const StyledClose = styled(Close)`
  position: absolute;
  margin-left: 10px;
`;

const StyledArrowLeft = styled(ChevronLeft)`
  position: absolute;
  margin-left: 10px;
`;

const FlexContent = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  min-height: 44px;
  padding-bottom: 5px;
`;

const FixedHeaderTitle = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  text-align: center;
  z-index: 0;
  max-width: ${({ labelAction }) => (labelAction ? '50%' : '70%')};
  margin: 0 auto;
`;

const FixedHeaderEditAction = styled(SimpleButton)`
  position: absolute;
  right: 5px;
  top: 10px;
  z-index: 1;
  background: none;
`;

function FixedHeader({
  id,
  label,
  onClose,
  actionLabel,
  onActionClick,
  children,
  icon,
  isModal,
  showIcon,
  showLogo,
  ...props
}) {
  let iconComponent;
  if (icon) iconComponent = <Icon onClick={onClose} src={icon} />;
  else
    iconComponent = isModal ? (
      <StyledClose
        onClick={onClose}
        aria-label="Cerrar"
        onKeyUp={e => onKeyUpFunction(e, onClose)}
        onKeyDown={e => onKeyDownFunction(e, onClose)}
        tabIndex={0}
        size="medium"
        role="button"
      />
    ) : (
      <StyledArrowLeft
        onClick={onClose}
        aria-label="Volver"
        onKeyUp={e => onKeyUpFunction(e, onClose)}
        onKeyDown={e => onKeyDownFunction(e, onClose)}
        tabIndex={0}
        size="medium"
        role="button"
      />
    );
  return (
    <StyledFixedHeader id={id} {...props}>
      <FlexContent>
        {showIcon && iconComponent}
        {showLogo && <HeaderLogo />}

        <FixedHeaderTitle labelAction={!!actionLabel}>{label}</FixedHeaderTitle>

        {!!actionLabel && (
          <FixedHeaderEditAction
            onClick={onActionClick}
            size={SIZE.SMALL}
            label={actionLabel}
            color="tertiary"
          />
        )}
      </FlexContent>
      {children && <FlexContent>{children}</FlexContent>}
    </StyledFixedHeader>
  );
}

FixedHeader.propTypes = {
  id: PropTypes.string,
  actionLabel: PropTypes.string,
  label: PropTypes.string,
  onActionClick: PropTypes.func,
  children: PropTypes.node,
  icon: PropTypes.string,
  isModal: PropTypes.bool,
  showIcon: PropTypes.bool,
  showLogo: PropTypes.bool,
  onClose: (props, propName, componentName) => {
    if (
      props.showIcon &&
      (!props[propName] || typeof props[propName] !== 'function')
    ) {
      return new Error(
        `Failed prop type: The prop \`${propName}\` is marked as required in
          \`${componentName}\`, but its value is \`${typeof props[propName]}\``,
      );
    }
    return null;
  },
};

FixedHeader.defaultProps = {
  id: FIXED_HEADER_ID,
  isModal: false,
  showIcon: true,
  children: null,
  label: '',
  showLogo: false,
};

export default FixedHeader;
